import React from 'react'
import { FormattedMessage, useIntl, IntlShape } from 'react-intl'
import { Header, Item, Panel } from '.'
import { PanelProps } from './panel'
import { HeaderProps } from './header'
import { ItemProps } from './item'
import {
  CustomSlidingTutorialProps,
  SlidingTutorialContent
} from './sliding-tutorial-content'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { getLibrariesRoute, getApplicationLibrariesRoute } from '../route'
import appVulnsCalendarIcon from '../../../images/tutorial/appVulnsCalendarIcon.png'
import appVulnsFixIcon from '../../../images/tutorial/appVulnsFixIcon.png'
import appVulnsSearchIcon from '../../../images/tutorial/appVulnsSearchIcon.png'

const itemsConfig = (intl: IntlShape): ItemProps[] => [
  {
    title: {
      id: 'title_vulnsSlidingTutorial_discoverVulnerabilities',
      defaultMessage: 'Discover Vulnerabilities'
    },
    description: {
      id: 'detail_vulnsSlidingTutorial_discoverVulnerabilities',
      defaultMessage:
        'Contrast has real-time visibility into data that flows through your application. When we detect a security flaw in the code path, it’s immediately reported here.'
    },
    imagePath: appVulnsCalendarIcon,
    circleIndicators: [
      {
        selector: '[tutorial-highlight="appVulnerabilities"]',
        radius: 66,
        yOffset: -46,
        xOffset: -6
      }
    ]
  },
  {
    title: {
      id: 'title_vulnsSlidingTutorial_analyzeFindings',
      defaultMessage: 'Analyze Findings'
    },
    description: {
      id: 'detail_vulnsSlidingTutorial_analyzeFindings',
      defaultMessage:
        'Triaging vulnerability findings can depend on several factors, such as your security concerns or risk to the application. They’ll be presented with a severity to help you prioritize.<div><a>Learn more</a> about analysis</div>',
      values: {
        div: (...msg) => <div>{msg}</div>,
        a: (...msg) => (
          <a
            href={intl.formatMessage({ id: 'link_analyzeEvents' })}
            target="_blank"
          >
            {msg}
          </a>
        )
      }
    },
    imagePath: appVulnsSearchIcon,
    circleIndicators: [
      {
        selector: '[tutorial-highlight="vulnSeverity"]',
        radius: 66,
        yOffset: -68,
        xOffset: -32
      }
    ]
  },
  {
    title: {
      id: 'title_vulnsSlidingTutorial_takeAction',
      defaultMessage: 'Take Action'
    },
    description: {
      id: 'detail_vulnsSlidingTutorial_takeAction',
      defaultMessage:
        'Drill into any vulnerability to find detailed information on solutions or techniques to resolve a vulnerability.<div>Get more on <a>how to fix</a></div>',
      values: {
        div: (...msg) => <div>{msg}</div>,
        a: (...msg) => (
          <a
            href={intl.formatMessage({ id: 'link_fixVulnerabilities' })}
            target="_blank"
          >
            {msg}
          </a>
        )
      }
    },
    imagePath: appVulnsFixIcon,
    circleIndicators: [
      {
        selector: '[tutorial-highlight="vulnTitle"]',
        radius: 66,
        yOffset: -68,
        xOffset: 0
      }
    ]
  }
]

const headerConfig: HeaderProps = {
  title: {
    id: 'title_vulnsSlidingTutorial_getRidWeaknessess',
    defaultMessage: 'Get Rid of Weaknessess'
  },
  subtitle: {
    id: 'detail_vulnsSlidingTutorial_getRidWeaknessess',
    defaultMessage: 'Don’t let your application be compromised.'
  }
}

const panelConfig = (intl: IntlShape): PanelProps => ({
  renderHeader: () => {
    return <Header {...headerConfig} />
  },
  renderBody: () =>
    itemsConfig(intl).map((item, index) => <Item key={index} {...item} />),
  renderFooter: (renderFooterProps) => {
    return (
      <Button {...renderFooterProps}>
        <FormattedMessage
          id="action_tellMeMore"
          defaultMessage="Got it. Tell Me More"
        />
      </Button>
    )
  }
})

export function VulnerabilitiesSlidingTutorial(
  props: CustomSlidingTutorialProps
) {
  const intl = useIntl()
  const librariesLink = props.appId
    ? getApplicationLibrariesRoute(props.orgUuid, props.appId)
    : getLibrariesRoute(props.orgUuid)
  return (
    <SlidingTutorialContent
      isOpen={props.isOpen}
      renderPanel={(renderPanelProps) => {
        return <Panel {...renderPanelProps} {...panelConfig(intl)} />
      }}
      tutorialKey={props.tutorialKey}
      orgUuid={props.orgUuid}
      yOffset={props.yOffset}
      route={librariesLink}
      zIndex={props.zIndex}
    />
  )
}
